import { useCallback, useMemo, useState } from 'react';

/** Total number of steps in the guardian onboarding wizard. */
export const ONBOARDING_TOTAL_STEPS = 4;

type UseOnboardingOptions = {
  /** Called when the user finishes the last step (e.g. navigate to the dashboard). */
  onComplete?: () => void;
  /** 0-based step to start on — used to resume where the guardian left off. */
  initialStep?: number;
};

/**
 * Drives the onboarding wizard: which step is active and how to move between them.
 * Step navigation lives here; leaving the wizard is delegated to the
 * `onComplete` callback supplied by the screen. Both "Continue" and "Skip"
 * advance one step (the screen records which via the onboarding API).
 */
export function useOnboarding({ onComplete, initialStep = 0 }: UseOnboardingOptions = {}) {
  const [currentStep, setCurrentStep] = useState(initialStep);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === ONBOARDING_TOTAL_STEPS - 1;

  const goNext = useCallback(() => {
    if (isLastStep) {
      onComplete?.();
      return;
    }
    setCurrentStep(step => Math.min(ONBOARDING_TOTAL_STEPS - 1, step + 1));
  }, [isLastStep, onComplete]);

  const goBack = useCallback(() => {
    setCurrentStep(step => Math.max(0, step - 1));
  }, []);

  return useMemo(
    () => ({
      currentStep,
      totalSteps: ONBOARDING_TOTAL_STEPS,
      isFirstStep,
      isLastStep,
      goNext,
      goBack,
    }),
    [currentStep, isFirstStep, isLastStep, goNext, goBack],
  );
}

export default useOnboarding;
